import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { ApiError } from "@teamblob/api-client";
import { useAuthStore } from "../stores/auth-store.js";
import { useBodyClass } from "../hooks/use-body-class.js";
import {
  AuthField,
  AuthErrorBanner,
} from "../components/desktop/auth-primitives.js";
import { Button } from "@teamblob/ui/components";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/register")({
  component: RegisterPage,
});

// ---------------------------------------------------------------------------
// Form schema (client-side, mirrors the backend RegisterBodySchema)
// ---------------------------------------------------------------------------

const registerSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required."),
  lastName: z.string().trim().min(1, "Last name is required."),
  email: z.string().email("Enter a valid email address."),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters.")
    .max(128, "Password must be at most 128 characters."),
});

type FormData = z.infer<typeof registerSchema>;
type FormErrors = Partial<Record<keyof FormData, string>>;

function RegisterPage() {
  useBodyClass("auth-scroll");
  const navigate = useNavigate();
  const register = useAuthStore((s) => s.register);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});

  const validate = (): boolean => {
    const result = registerSchema.safeParse({
      firstName,
      lastName,
      email,
      password,
    });
    if (result.success) {
      setFieldErrors({});
      return true;
    }
    const errors: FormErrors = {};
    for (const issue of result.error.issues) {
      const key = issue.path[0] as keyof FormData;
      if (key && !errors[key]) {
        errors[key] = issue.message;
      }
    }
    setFieldErrors(errors);
    return false;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      await register({ firstName, lastName, email, password });
      // No session is established — gate access on verification. Send the user
      // to the "check your inbox" screen; they verify, then sign in.
      void navigate({
        to: "/verify-email-sent",
        search: { email },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.statusCode === 409) {
          setFieldErrors((prev) => ({
            ...prev,
            email: "An account with this email already exists.",
          }));
        } else if (err.statusCode === 422 || err.statusCode === 400) {
          setFormError(
            err.details?.[0]?.message ??
              err.message ??
              "Validation failed. Please check your inputs.",
          );
        } else {
          setFormError("Something went wrong. Please try again.");
        }
      } else {
        setFormError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-window-face)] p-8 shadow-[var(--shadow-window)]">
        <h1 className="mb-1 text-[22px] font-semibold text-[var(--color-text-strong)]">
          Create your account
        </h1>
        <p className="mb-6 text-[13px] text-[var(--color-text-muted)]">
          Free to start. You&apos;ll set up your workspace after confirming your
          email.
        </p>

        {formError !== null ? (
          <div className="mb-4">
            <AuthErrorBanner message={formError} />
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* First + last name */}
          <div className="grid grid-cols-2 gap-3">
            <AuthField
              label="First name"
              type="text"
              autoComplete="given-name"
              required
              placeholder="Jane"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={fieldErrors.firstName}
            />
            <AuthField
              label="Last name"
              type="text"
              autoComplete="family-name"
              required
              placeholder="Smith"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={fieldErrors.lastName}
            />
          </div>

          {/* Email */}
          <AuthField
            label="Work email"
            type="email"
            autoComplete="email"
            required
            placeholder="jane@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldErrors.email}
          />

          {/* Password */}
          <AuthField
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            placeholder="8+ characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors.password}
            suffix={
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text-body)]"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            }
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            isLoading={submitting}
            disabled={submitting}
          >
            {submitting ? "Creating account…" : "Create account"}
          </Button>

          <p className="text-center text-[11px] text-[var(--color-text-muted)]">
            By creating an account you agree to our Terms of Service and Privacy
            Policy.
          </p>
        </form>

        <div className="mt-5 border-t border-[var(--color-border-subtle)] pt-4 text-center text-[12px]">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-[var(--color-text-link)] hover:underline"
          >
            Sign in
          </Link>
        </div>
      </div>
    </main>
  );
}
